import html2pdf from "html2pdf.js";

const PDF_EXPORT_CLASS = "fsr-pdf-export-container";
const PDF_EXPORT_ROOT_CLASS = "fsr-pdf-export";
const SPACER_CLASS = "fsr-pdf-spacer";

// Letter, 0.5in margins. html2pdf converts the inner page with jsPDF's k (72)
// and 96 CSS px per inch: floor(10 * 96) = 960.
const PAGE_HEIGHT_IN = 11;
const MARGIN_IN = 0.5;
const PX_PER_IN = 96;
const PAGE_HEIGHT_PX = Math.floor((PAGE_HEIGHT_IN - MARGIN_IN * 2) * PX_PER_IN);
const KEEP_WITH_NEXT_PX = PX_PER_IN;

const HEADING_SELECTOR = [
    ".fsr-report-section > h2",
    ".fsr-report-ticket > h3",
    ".fsr-report-service > h3",
    ".fsr-event-timeline > h4",
    ".fsr-service-values > h4",
].join(", ");

const KEEP_TOGETHER_SELECTOR = [
    ".fsr-report-table",
    ".fsr-event-row",
    ".fsr-service-values li",
].join(", ");

const LIGHT_THEME_VARS = {
    "--app-bg": "#ffffff",
    "--text-color": "#111827",
    "--text-muted": "#64748b",
    "--text-subtle": "#94a3b8",
    "--card-bg": "#ffffff",
    "--card-border": "#e2e8f0",
    "--surface-muted": "#f8fafc",
    "--panel-bg": "#f8fafc",
    "--header-bg": "#eeeeee",
    "--header-border": "#dddddd",
};

function applyLightTheme(el) {
    if (!el || !el.style) return;
    el.setAttribute("data-theme", "light");
    el.style.colorScheme = "light";
    Object.entries(LIGHT_THEME_VARS).forEach(([key, value]) => {
        el.style.setProperty(key, value);
    });
}

function contentTop(el, root) {
    return el.getBoundingClientRect().top - root.getBoundingClientRect().top;
}

function insertSpacer(el, heightPx) {
    if (heightPx < 1) return;
    const pad = document.createElement("div");
    pad.className = SPACER_CLASS;
    pad.setAttribute("aria-hidden", "true");
    pad.style.display = "block";
    pad.style.height = `${heightPx}px`;
    el.parentNode.insertBefore(pad, el);
}

function isSkippedSibling(el) {
    return el.classList.contains(SPACER_CLASS) || el.classList.contains("fsr-ticket-desc");
}

// The details table is the block a ticket title has to keep. A one-line
// description sits between them and is not enough on its own.
function nextBlock(el) {
    let next = el.nextElementSibling;
    while (next && isSkippedSibling(next)) next = next.nextElementSibling;
    return next;
}

function pageOf(el, root, pageHeightPx) {
    return Math.floor(contentTop(el, root) / pageHeightPx);
}

function pushDown(el, root, pageHeightPx) {
    const intoPage = contentTop(el, root) % pageHeightPx;
    const spaceLeft = pageHeightPx - intoPage;
    if (spaceLeft < 1 || spaceLeft >= pageHeightPx - 1) return false;
    insertSpacer(el, spaceLeft);
    return true;
}

function placeBreaks(root, pageHeightPx) {
    const blocks = root.querySelectorAll(
        `${HEADING_SELECTOR}, ${KEEP_TOGETHER_SELECTOR}`
    );
    let moved = false;
    Array.prototype.forEach.call(blocks, (el) => {
        const height = el.getBoundingClientRect().height;
        if (height <= 0 || height >= pageHeightPx) return;

        const intoPage = contentTop(el, root) % pageHeightPx;
        const spaceLeft = pageHeightPx - intoPage;
        if (spaceLeft >= pageHeightPx - 1) return;

        let needed = height;
        if (el.matches(HEADING_SELECTOR)) {
            const next = nextBlock(el);
            const nextHeight = next ? next.getBoundingClientRect().height : KEEP_WITH_NEXT_PX;
            const gap = next ? contentTop(next, root) - contentTop(el, root) : height;
            const keepWhole = next
                && next.matches(KEEP_TOGETHER_SELECTOR)
                && nextHeight > 0
                && gap + nextHeight < pageHeightPx;
            needed = keepWhole
                ? gap + nextHeight
                : height + Math.min(nextHeight, KEEP_WITH_NEXT_PX);
        }

        if (spaceLeft + 0.5 < needed && pushDown(el, root, pageHeightPx)) moved = true;
    });
    return moved;
}

function pullHeadings(root, pageHeightPx) {
    let moved = false;
    Array.prototype.forEach.call(root.querySelectorAll(HEADING_SELECTOR), (heading) => {
        const block = nextBlock(heading);
        if (!block) return;

        const before = block.previousElementSibling;
        const inner = block.firstElementChild;
        const spacer = before && before.classList.contains(SPACER_CLASS)
            ? before
            : inner && inner.classList.contains(SPACER_CLASS)
                ? inner
                : null;
        if (!spacer) return;

        const follow = spacer === inner ? spacer.nextElementSibling : block;
        if (!follow) return;
        if (pageOf(follow, root, pageHeightPx) <= pageOf(heading, root, pageHeightPx)) return;

        const group = contentTop(follow, root)
            + follow.getBoundingClientRect().height
            - contentTop(heading, root)
            - spacer.getBoundingClientRect().height;
        if (group >= pageHeightPx) return;

        spacer.parentNode.removeChild(spacer);
        if (pushDown(heading, root, pageHeightPx)) moved = true;
    });
    return moved;
}

/**
 * html2pdf paints one canvas and slices it, so break-after:avoid never fires.
 * A heading keeps its details table (skipping a one-line description). Short
 * tables, event rows, and value lines stay whole. Tall cards still flow.
 * Spacers are plain blocks, and the export uses legacy page breaks so
 * html2pdf does not add a second set.
 */
export function keepPdfBlocksTogether(root, pageHeightPx = PAGE_HEIGHT_PX) {
    if (!root) return;

    placeBreaks(root, pageHeightPx);
    for (let pass = 0; pass < 4; pass += 1) {
        const pulled = pullHeadings(root, pageHeightPx);
        const pushed = placeBreaks(root, pageHeightPx);
        if (!pulled && !pushed) break;
    }
}

/**
 * Export a DOM element to a PDF file. Clones the node into an off-screen
 * container with print-friendly light-mode styles so only the report content
 * is captured (never dark theme).
 */
export async function exportElementToPdf(element, filename) {
    if (!element) {
        throw new Error("Nothing to export");
    }

    const container = document.createElement("div");
    container.className = PDF_EXPORT_CLASS;
    applyLightTheme(container);

    const clone = element.cloneNode(true);
    clone.classList.add(PDF_EXPORT_ROOT_CLASS);
    applyLightTheme(clone);
    container.appendChild(clone);
    document.body.appendChild(container);
    keepPdfBlocksTogether(container);

    try {
        await html2pdf()
            .set({
                margin: [0.5, 0.5, 0.5, 0.5],
                filename,
                image: { type: "jpeg", quality: 0.95 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: "#ffffff",
                    onclone: (clonedDoc) => {
                        applyLightTheme(clonedDoc.documentElement);
                        applyLightTheme(clonedDoc.body);
                        clonedDoc
                            .querySelectorAll(
                                `.${PDF_EXPORT_CLASS}, .${PDF_EXPORT_ROOT_CLASS}`
                            )
                            .forEach(applyLightTheme);
                    },
                },
                jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
                // css mode ignores break-after:avoid and would pad again on top of
                // the spacers keepPdfBlocksTogether already inserted.
                pagebreak: { mode: ["legacy"] },
            })
            // Export the report clone (has .fsr-pdf-export), not the off-screen
            // wrapper — otherwise position:fixed / left:-10000px would be cloned.
            .from(clone)
            .save();
    } finally {
        document.body.removeChild(container);
    }
}
